import {
  APP_INITIALIZER,
  ApplicationConfig,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  Optional,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  MatDialogModule,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ResponseCheckInterceptor } from './Core/interceptors/response-check/response-check.interceptor';
import { LanguageHeaderInterceptor } from './Core/interceptors/language-header/language-header.interceptor';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import { register } from 'register-service-worker';

// Import ngx-translate modules
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable } from 'rxjs';

// Custom Translation Loader that works with any version
export class CustomTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json',
  ) {}

  public getTranslation(lang: string): Observable<any> {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`);
  }
}

// Factory function for the loader
export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  // Option 1: Use the default TranslateHttpLoader without parameters
  // return new TranslateHttpLoader(http);

  // Option 2: Use our custom loader with the paths we want
  return new CustomTranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Import for platform detection
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';

// Import Clarity service
import { ClarityService } from './Shared/_services/clarity/clarity.service';
import { StorageService } from './Core/_services/storage/storage.service';

// Import Analytics service
import { AnalyticsService } from './Shared/_services/analytics/analytics.service';
import { LanguageService } from './Shared/services/language/language.service';
import { RouterLocalePatchService } from './Core/locale/router-locale-patch.service';
import { isValidLocale, LocaleCode } from './Core/locale/locale.constants';

// App initializer for setting default language (SSR-safe)
// Simplified app initializer - just set up translation module
export function initializeApp(translate: TranslateService, platformId: Object) {
  return (): Promise<any> => {
    const availableLanguages = ['en', 'ar', 'fr', 'es'];
    translate.addLangs(availableLanguages);
    translate.setDefaultLang('en');

    // Don't use any language yet - let LanguageService handle it
    return Promise.resolve();
  };
}

// Factory function for Clarity initialization
export function initializeClarity(clarityService: ClarityService) {
  return (): void => {
    if ((environment as any).clarityEnabled === false) {
      return;
    }

    clarityService.initialize();
  };
}

// Factory function for Analytics initialization
export function initializeAnalytics(analyticsService: AnalyticsService) {
  return (): void => {
    analyticsService.initialize();
  };
}
function getLocaleFromPath(pathname: string | null | undefined): LocaleCode | null {
  const firstSegment = (pathname ?? '/')
    .split('/')
    .filter(Boolean)[0]
    ?.toLowerCase();
  return isValidLocale(firstSegment) ? firstSegment : null;
}

export function initializeLanguageServiceFromUrl(
  languageService: LanguageService,
  platformId: Object,
  localeFromUrl?: string
) {
  return (): Promise<any> => {
    const localeFromBrowserUrl = isPlatformBrowser(platformId)
      ? getLocaleFromPath(window.location.pathname)
      : null;

    const localeFromServerUrl = getLocaleFromPath(
      localeFromUrl ? `/${localeFromUrl}` : null
    );

    const effectiveLocale = localeFromBrowserUrl ?? localeFromServerUrl;
    if (effectiveLocale) {
      languageService.setLanguageFromLocaleCode(effectiveLocale);
      return Promise.resolve();
    }

    const savedLang = languageService.getSelectedLanguage();
    if (savedLang && Object.keys(savedLang).length > 0) {
      languageService.setLanguage(savedLang);
    }

    return Promise.resolve();
  };
}

export function initializeRouterLocalePatch(
  routerLocalePatchService: RouterLocalePatchService
) {
  return (): void => {
    routerLocalePatchService.patchRouter();
  };
}
// Only initialize Sentry in browser environment (SSR-safe check)
if (
  typeof window !== 'undefined' &&
  environment.sentryDNS &&
  (environment as any).sentryEnabled !== false
) {
  const sentryTracingEnabled = (environment as any).sentryTracingEnabled !== false;
  const sentryProfilingEnabled =
    (environment as any).sentryProfilingEnabled !== false;
  const sentryReplayEnabled = (environment as any).sentryReplayEnabled !== false;

  const integrations = [
    // Only promote real errors to Sentry. Capturing info/log floods issues with
    // benign console output (e.g. Pusher state changes, service worker logs).
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ];
  if (sentryTracingEnabled) {
    integrations.push(Sentry.browserTracingIntegration());
  }
  if (sentryProfilingEnabled) {
    integrations.push(Sentry.browserProfilingIntegration());
  }
  if (sentryReplayEnabled) {
    integrations.push(Sentry.replayIntegration());
  }

  Sentry.init({
    dsn: environment.sentryDNS,
    environment: (environment as any).sentryEnv || 'unknown',
    integrations,
    tracesSampleRate: sentryTracingEnabled
      ? environment.production
        ? 0.1
        : 1.0
      : 0,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: sentryReplayEnabled ? 0.01 : 0,
    replaysOnErrorSampleRate: sentryReplayEnabled ? 1.0 : 0,
    profilesSampleRate: sentryProfilingEnabled
      ? environment.production
        ? 0.1
        : 1.0
      : 0,
    beforeSend(event) {
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (
          error?.type === 'ChunkLoadError' ||
          error?.type === 'ResizeObserver loop limit exceeded'
        ) {
          return null;
        }
      }
      return event;
    },
  });
}

// Enable production mode (safe during SSR)
if (environment.production) {
  enableProdMode();
}

// Register service worker only in browser environment (SSR-safe check)
if (typeof window !== 'undefined' && environment.production) {
  register('ngsw-worker.js', {
    registrationOptions: {
      scope: './',
    },
    ready: (registration: any) => {
      console.log('Service worker is active.');
    },
    registered: (registration: any) => {
      console.log('Service worker has been registered.');
    },
    cached: (registration: any) => {
      console.log('Content has been cached for offline use.');
    },
    updatefound: (registration: any) => {
      console.log('New content is downloading.');
    },
    updated: (registration: any) => {
      console.log('New content is available; please refresh.');
    },
    offline: () => {
      console.log(
        'No internet connection found. App is running in offline mode.',
      );
    },
    error: (error: any) => {
      console.error('Error during service worker registration:', error);
    },
  });
}
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    importProvidersFrom(
      ToastrModule.forRoot(),
      MatDialogModule,
      MatSelectModule,
      CarouselModule,
      // Add TranslateModule configuration
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
    ),
    // Add TranslateService provider and initializer with PLATFORM_ID
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [TranslateService, PLATFORM_ID],
      multi: true,
    },
    // Add Clarity initialization
    {
      provide: APP_INITIALIZER,
      useFactory: initializeClarity,
      deps: [ClarityService],
      multi: true,
    },
    // Add Analytics initialization
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAnalytics,
      deps: [AnalyticsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguageServiceFromUrl,
      deps: [LanguageService, PLATFORM_ID, [new Optional(), 'LOCALE_FROM_URL']],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRouterLocalePatch,
      deps: [RouterLocalePatchService],
      multi: true,
    },
    // Sentry error handler - SSR-safe with platform check
    ...(environment.sentryDNS && (environment as any).sentryEnabled !== false
      ? [
          {
            provide: ErrorHandler,
            useFactory: (platformId: Object) => {
              if (isPlatformBrowser(platformId)) {
                return Sentry.createErrorHandler({ showDialog: false });
              }
              return new ErrorHandler();
            },
            deps: [PLATFORM_ID],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: (platformId: Object) => () => {
              if (
                isPlatformBrowser(platformId) &&
                (environment as any).sentryTracingEnabled !== false
              ) {
                Sentry.browserTracingIntegration();
              }
            },
            deps: [PLATFORM_ID],
            multi: true,
          },
        ]
      : []),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseCheckInterceptor,
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        closeOnNavigation: true,
        maxWidth: '90vw',
      },
    },
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideAnimations(),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
      }),
    ),
  ],
};
